import type { KeyboardEvent, MouseEvent } from 'react'

type MouseEventHandler = (e: MouseEvent) => void
type KeyboardEventHandler = (e: KeyboardEvent) => void

/**
 * Wraps a callback to stop event propagation before executing.
 * Useful for nested clickable elements.
 */
export const withStopPropagation =
  (callback: () => void): MouseEventHandler =>
  (e) => {
    e.stopPropagation()
    callback()
  }

/**
 * Wraps a callback to prevent default behavior before executing.
 */
export const withPreventDefault =
  (callback: () => void): MouseEventHandler =>
  (e) => {
    e.preventDefault()
    callback()
  }

/**
 * Wraps a callback to both stop propagation and prevent default.
 */
export const withStopAndPrevent =
  (callback: () => void): MouseEventHandler =>
  (e) => {
    e.stopPropagation()
    e.preventDefault()
    callback()
  }

/**
 * Creates a keyboard handler for Enter and Space key activation.
 * Useful for making non-button elements keyboard accessible.
 */
export const withKeyboardActivation =
  (callback: () => void): KeyboardEventHandler =>
  (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      callback()
    }
  }
